import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Camera, ChevronDown, Images, Trash2, Upload, UserRound } from 'lucide-react';
import { MediaAsset } from '../../types/media';
import { mediaService } from '../../services/mediaService';
import { MediaGallery } from '../media/MediaGallery';
import { AuthImage } from '../media/AuthImage';
import { Stakeholder, CreateStakeholderDto, UpdateStakeholderDto } from '../../types/stakeholder';
import { InfluenceScoresEditor } from '../influences/InfluenceScoresEditor';
import { listTaxonomies, TaxonomyItem } from '../../services/taxonomiesService';
import { AssignmentManager, validateAssignmentData } from './AssignmentManager';
import { Assignment } from '../../types/stakeholder';
import { useToast } from '../common';
import { CategoryCombobox } from '../common/CategoryCombobox';
import { autoCategory, findCategory, suggestCategories } from './categorySuggestions';
import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogHeader,
  Field,
  Input,
  Menu,
  SectionHeader,
  Textarea,
} from '../ui';
import type { MenuItem } from '../ui';

interface StakeholderFormProps {
  stakeholder?: Stakeholder;
  prefillData?: any;
  onSave: (
    data: CreateStakeholderDto | UpdateStakeholderDto,
    assignment?: { organizationId?: string; role?: string; department?: string; startDate?: string; endDate?: string; isCurrent?: boolean },
    extras?: { profileImageFile?: File | null; profileImageId?: string }
  ) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  includeOrganizationFields?: boolean;
  influenceEntityId?: string;
  onInfluenceScoresChange?: (scores: { categoryId: string; score: number }[]) => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const StakeholderForm: React.FC<StakeholderFormProps> = ({
  stakeholder,
  prefillData,
  onSave,
  onCancel,
  isLoading = false,
  includeOrganizationFields = false,
  influenceEntityId,
  onInfluenceScoresChange,
  onDirtyChange,
}) => {
  const { showError, showWarning } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    professionalEmail: '',
    personalEmail: '',
    professionalPhone: '',
    personalPhone: '',
    location: '',
    bio: '',
    facebookUrl: '',
    linkedinUrl: '',
    twitterUrl: '',
    telegramUrl: '',
    whatsapp: '',
    category: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [stakeholderCategories, setStakeholderCategories] = useState<TaxonomyItem[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Profile image state
  const [profileImagePreview, setProfileImagePreview] = useState<string>('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImageId, setProfileImageId] = useState<string | undefined>(undefined);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Assignment state
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [pendingAssignment, setPendingAssignment] = useState<{
    organizationId?: string; role?: string; department?: string;
    startDate?: string; endDate?: string; isCurrent?: boolean;
  } | null>(null);

  // Collapsible sections (null = follow the default)
  const [socialExpanded, setSocialExpanded] = useState<boolean | null>(null);
  const [influenceExpanded, setInfluenceExpanded] = useState(!!influenceEntityId);

  // Track mobile viewport for section collapse defaults
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Populate form from stakeholder or prefillData (once per distinct prefill, so a parent
  // re-render never overwrites what the user has already changed)
  const appliedPrefillRef = useRef<string | null>(null);
  const categoryTouchedRef = useRef(false);
  useEffect(() => {
    if (!stakeholder && prefillData) {
      const signature = JSON.stringify(prefillData);
      if (appliedPrefillRef.current === signature) return;
      appliedPrefillRef.current = signature;
      categoryTouchedRef.current = false;
    }
    if (stakeholder) {
      setFormData({
        firstName: stakeholder.firstName || '',
        lastName: stakeholder.lastName || '',
        professionalEmail: stakeholder.professionalEmail || '',
        personalEmail: stakeholder.personalEmail || '',
        professionalPhone: stakeholder.professionalPhone || '',
        personalPhone: stakeholder.personalPhone || '',
        location: stakeholder.location || '',
        bio: stakeholder.bio || '',
        facebookUrl: stakeholder.facebookUrl || '',
        linkedinUrl: stakeholder.linkedinUrl || '',
        twitterUrl: stakeholder.twitterUrl || '',
        telegramUrl: stakeholder.telegramUrl || '',
        whatsapp: stakeholder.whatsapp || '',
        category: stakeholder.category || '',
      });
      setAssignments(stakeholder.assignments || []);
      if (stakeholder.profileImageId) {
        setProfileImageId(stakeholder.profileImageId);
        setProfileImagePreview(mediaService.getViewUrl(stakeholder.profileImageId));
      } else if ((stakeholder as any).profileImageUrl) {
        setProfileImagePreview((stakeholder as any).profileImageUrl);
      }
    } else if (prefillData) {
      setFormData({
        firstName: prefillData.firstName || '',
        lastName: prefillData.lastName || '',
        professionalEmail: prefillData.professionalEmail || '',
        personalEmail: prefillData.personalEmail || '',
        professionalPhone: prefillData.professionalPhone || '',
        personalPhone: prefillData.personalPhone || '',
        location: prefillData.location || '',
        bio: prefillData.bio || '',
        facebookUrl: prefillData.facebookUrl || '',
        linkedinUrl: prefillData.linkedinUrl || '',
        twitterUrl: prefillData.twitterUrl || '',
        telegramUrl: prefillData.telegramUrl || '',
        whatsapp: prefillData.whatsapp || '',
        category: prefillData.category || '',
      });
      if (prefillData.businessCardMetadata?.imageId) {
        setProfileImageId(prefillData.businessCardMetadata.imageId);
        setProfileImagePreview(mediaService.getViewUrl(prefillData.businessCardMetadata.imageId));
      }
    }
  }, [stakeholder, prefillData]);

  // Load categories
  useEffect(() => {
    (async () => {
      try { setStakeholderCategories(await listTaxonomies('stakeholder_category')); } catch { }
    })();
  }, []);

  // Category from a business card: the AI's pick, or a suggestion from the job title / organization
  const categorySuggestions = useMemo(
    () =>
      !stakeholder && prefillData
        ? suggestCategories(
            {
              category: prefillData.category,
              role: prefillData.role,
              department: prefillData.department,
              organization: prefillData.organizationName,
            },
            stakeholderCategories,
          )
        : [],
    [stakeholder, prefillData, stakeholderCategories],
  );
  const [categoryPicked, setCategoryPicked] = useState<'card' | 'suggested' | null>(null);
  const autoCategoryRef = useRef<string | null>(null);
  useEffect(() => {
    if (stakeholder || !prefillData || !stakeholderCategories.length) return;
    const signature = JSON.stringify(prefillData);
    if (autoCategoryRef.current === signature) return;
    autoCategoryRef.current = signature;
    // The user already chose while categories were loading
    if (categoryTouchedRef.current) return;
    const chosen = autoCategory(categorySuggestions);
    if (!chosen) {
      setCategoryPicked(null);
      return;
    }
    setFormData((prev) => ({ ...prev, category: chosen.option.label }));
    setCategoryPicked(chosen.source === 'card' ? 'card' : 'suggested');
  }, [stakeholder, prefillData, stakeholderCategories, categorySuggestions]);

  // Dirty tracking
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const markDirty = () => {
    if (!isDirty) setIsDirty(true);
  };

  // ── Validation ──

  const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'firstName':
        if (!value.trim()) return 'First name is required';
        if (value.trim().length < 2) return 'Must be at least 2 characters';
        if (!/^[\p{L}\s'-]+$/u.test(value.trim())) return 'Contains invalid characters';
        return '';
      case 'lastName':
        if (!value.trim()) return 'Last name is required';
        if (value.trim().length < 2) return 'Must be at least 2 characters';
        if (!/^[\p{L}\s'-]+$/u.test(value.trim())) return 'Contains invalid characters';
        return '';
      case 'professionalEmail':
        if (value.trim() && !isValidEmail(value)) return 'Invalid email address';
        return '';
      case 'personalEmail':
        if (value.trim() && !isValidEmail(value)) return 'Invalid email address';
        return '';
      case 'professionalPhone':
      case 'personalPhone':
        if (value.trim() && !/^[+]?[0-9\s()-]{5,20}$/.test(value)) return 'Invalid phone number';
        return '';
      case 'bio':
        if (value.length > 1000) return `Too long (${value.length}/1000)`;
        return '';
      case 'location':
        if (value.length > 100) return 'Too long (max 100 characters)';
        return '';
      case 'category':
        if (!value.trim()) return 'Category is required';
        return '';
      case 'linkedinUrl':
        if (value.trim()) {
          if (!/^https?:\/\//i.test(value) && !value.includes('.')) return 'Invalid URL';
          if (!value.includes('linkedin.com')) return 'Must be a LinkedIn URL';
        }
        return '';
      case 'twitterUrl':
        if (value.trim()) {
          if (!/^https?:\/\//i.test(value) && !value.includes('.')) return 'Invalid URL';
          if (!value.includes('twitter.com') && !value.includes('x.com')) return 'Must be a Twitter/X URL';
        }
        return '';
      case 'facebookUrl':
        if (value.trim()) {
          if (!/^https?:\/\//i.test(value) && !value.includes('.')) return 'Invalid URL';
          if (!value.includes('facebook.com')) return 'Must be a Facebook URL';
        }
        return '';
      case 'telegramUrl':
        if (value.trim()) {
          if (!/^https?:\/\//i.test(value) && !value.includes('.')) return 'Invalid URL';
          if (!value.includes('t.me')) return 'Must be a Telegram URL';
        }
        return '';
      default:
        return '';
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate all fields
    for (const [key, value] of Object.entries(formData)) {
      const error = validateField(key, value);
      if (error) newErrors[key] = error;
    }

    // Contact validation
    const hasEmail = formData.professionalEmail.trim() || formData.personalEmail.trim();
    const hasPhone = formData.professionalPhone.trim() || formData.personalPhone.trim();
    if (!hasEmail && !hasPhone) {
      newErrors.contact = 'At least one contact method (email or phone) is required.';
    }

    setErrors(newErrors);
    setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

    if (Object.keys(newErrors).length > 0) {
      // Scroll to first error after state update
      requestAnimationFrame(() => {
        const firstErrorKey = Object.keys(newErrors)[0];
        const element = document.getElementById(firstErrorKey) || document.getElementsByName(firstErrorKey)[0];
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      return false;
    }
    return true;
  };

  const handleBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof typeof formData] || '');
    setErrors(prev => {
      const next = { ...prev };
      if (error) next[name] = error;
      else delete next[name];
      return next;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    markDirty();
    // Clear error on change if field was touched
    if (touched[name] && errors[name]) {
      const error = validateField(name, value);
      setErrors(prev => {
        const next = { ...prev };
        if (error) next[name] = error;
        else delete next[name];
        return next;
      });
    }
  };

  const handleCategoryChange = (value: string) => {
    setFormData(prev => ({ ...prev, category: value }));
    setCategoryPicked(null);
    categoryTouchedRef.current = true;
    markDirty();
    if (touched.category) {
      const error = validateField('category', value);
      setErrors(prev => {
        const next = { ...prev };
        if (error) next.category = error;
        else delete next.category;
        return next;
      });
    }
  };

  // ── Submit ──

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Validation Error', 'Please correct the fields marked in red.');
      return;
    }

    const submitData = {
      ...formData,
      professionalEmail: formData.professionalEmail || undefined,
      personalEmail: formData.personalEmail || undefined,
      professionalPhone: formData.professionalPhone || undefined,
      personalPhone: formData.personalPhone || undefined,
      location: formData.location || undefined,
      bio: formData.bio || undefined,
      facebookUrl: formData.facebookUrl || undefined,
      linkedinUrl: formData.linkedinUrl || undefined,
      twitterUrl: formData.twitterUrl || undefined,
      telegramUrl: formData.telegramUrl || undefined,
      whatsapp: formData.whatsapp || undefined,
      category: formData.category || undefined,
    };

    try {
      // Determine assignment data
      const singleAssignment = pendingAssignment || (assignments.length > 0 ? {
        organizationId: assignments[0].organizationId,
        role: assignments[0].role,
        department: assignments[0].department,
        startDate: assignments[0].startDate,
        endDate: assignments[0].endDate,
        isCurrent: assignments[0].isCurrent,
      } : null);

      // Validate assignment if provided
      if (singleAssignment && (singleAssignment.organizationId || singleAssignment.role)) {
        const validation = validateAssignmentData(singleAssignment);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, assignment: validation.errors.join(', ') }));
          showWarning('Assignment Issue', validation.errors.join(', '));
          return;
        }
      }

      await onSave(
        submitData,
        singleAssignment || undefined,
        { profileImageFile, profileImageId }
      );
      setIsDirty(false);
    } catch (error) {
      console.error('Form submission error:', error);
      showError('Error', 'Failed to save stakeholder');
    }
  };

  // ── Profile Image ──

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setProfileImageFile(file);
    if (file) {
      setProfileImagePreview(URL.createObjectURL(file));
      setProfileImageId(undefined);
    }
    markDirty();
  };

  const handleImageClear = () => {
    setProfileImageFile(null);
    setProfileImageId(undefined);
    setProfileImagePreview('');
    markDirty();
  };

  const handleMediaSelect = (media: MediaAsset) => {
    if (!mediaService.isImageFile(media.mimeType)) return;
    setProfileImageId(media.id);
    setProfileImagePreview(mediaService.getViewUrl(media.id));
    setProfileImageFile(null);
    setShowMediaPicker(false);
    markDirty();
  };

  // ── Render ──

  const isEditing = !!stakeholder;

  const handleInfluenceChange = useCallback(
    (scores: { categoryId: string; score: number }[]) => onInfluenceScoresChange?.(scores),
    [onInfluenceScoresChange]
  );

  type FieldName = keyof typeof formData;

  const fieldError = (name: FieldName) => (touched[name] ? errors[name] : undefined);

  // Shared props for the plain text inputs
  const inputProps = (name: FieldName) => ({
    id: name,
    name,
    value: formData[name],
    onChange: handleChange,
    onBlur: () => handleBlur(name),
    'aria-invalid': !!errors[name] && !!touched[name],
  });

  const socialFilled = SOCIAL_FIELDS.filter(name => formData[name].trim()).length;
  const socialHasError = SOCIAL_FIELDS.some(name => touched[name] && errors[name]);
  // Errors force the section open so scroll-to-error can reach the field
  const socialOpen = socialHasError || (socialExpanded ?? (!isMobile && socialFilled > 0));

  const hasPhoto = !!(profileImageFile || profileImageId || profileImagePreview);
  const openFilePicker = () => fileInputRef.current?.click();
  const photoMenuItems: MenuItem[] = [
    { label: hasPhoto ? 'Replace photo' : 'Upload photo', icon: Upload, onSelect: openFilePicker },
    ...(stakeholder?.id ? [{ label: 'Choose from media', icon: Images, onSelect: () => setShowMediaPicker(true) }] : []),
    ...(hasPhoto ? [{ label: 'Remove photo', icon: Trash2, danger: true, onSelect: handleImageClear }] : []),
  ];

  const submitLabel = isLoading ? (isEditing ? 'Saving…' : 'Creating…') : isEditing ? 'Save' : 'Create';

  return (
    <>
      <form ref={formRef} onSubmit={handleSubmit} className="space-y-4 font-ui text-[13px] text-ink" noValidate>
        {/* ═══ Identity ═══ */}
        <Card padded className="!overflow-visible">
          <SectionHeader title="Identity" />
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            {/* Profile photo */}
            <div className="relative flex-shrink-0 self-center sm:self-start">
              <button
                type="button"
                onClick={openFilePicker}
                aria-label={hasPhoto ? 'Replace photo' : 'Upload photo'}
                className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-neutral-100 text-neutral-400 transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 focus-visible:ring-offset-2 sm:h-[72px] sm:w-[72px]"
              >
                {profileImageId ? (
                  <AuthImage mediaId={profileImageId} alt="Profile" className="h-full w-full object-cover" />
                ) : profileImagePreview ? (
                  <img src={profileImagePreview} alt="Profile" className="h-full w-full object-cover" />
                ) : (
                  <UserRound className="h-8 w-8" strokeWidth={1.5} />
                )}
              </button>
              <span className="absolute -bottom-0.5 -right-0.5">
                {photoMenuItems.length > 1 ? (
                  <Menu
                    align={isMobile ? 'right' : 'left'}
                    items={photoMenuItems}
                    trigger={(props) => <CameraButton label="Photo options" {...props} />}
                  />
                ) : (
                  <CameraButton label="Upload photo" onClick={openFilePicker} />
                )}
              </span>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
            </div>

            <div className="grid grid-cols-1 min-w-0 flex-1 gap-3 sm:grid-cols-2">
              <Field label="First name" htmlFor="firstName" required error={fieldError('firstName')}>
                <Input type="text" {...inputProps('firstName')} placeholder="First name" autoComplete="off" />
              </Field>
              <Field label="Last name" htmlFor="lastName" required error={fieldError('lastName')}>
                <Input type="text" {...inputProps('lastName')} placeholder="Last name" autoComplete="off" />
              </Field>
              <Field
                label="Category"
                htmlFor="category"
                required
                error={fieldError('category')}
                hint={categoryPicked === 'card' ? 'Picked from the card' : categoryPicked === 'suggested' ? 'Suggested from the job title' : undefined}
              >
                <CategoryCombobox
                  value={formData.category}
                  onChange={handleCategoryChange}
                  options={stakeholderCategories}
                  error={touched.category ? errors.category : undefined}
                  placeholder="Select or type"
                />
                {categorySuggestions.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" aria-label="Suggested categories">
                    {categorySuggestions.map(({ option }) => {
                      const active = findCategory(stakeholderCategories, formData.category)?.id === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          aria-pressed={active}
                          onClick={() => handleCategoryChange(option.label)}
                          className={`inline-flex h-7 items-center rounded-full px-2.5 text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 ${
                            active ? 'bg-ink text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200 hover:text-ink'
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Field>
              <Field label="Location" htmlFor="location" error={fieldError('location')}>
                <Input type="text" {...inputProps('location')} placeholder="e.g. Conakry, Guinea" />
              </Field>
              <Field
                label="Bio"
                htmlFor="bio"
                className="sm:col-span-2"
                error={fieldError('bio')}
                hint={formData.bio.length > 0 ? `${formData.bio.length}/1000` : undefined}
              >
                <Textarea {...inputProps('bio')} rows={3} placeholder="Short bio or notes" />
              </Field>
            </div>
          </div>
        </Card>

        {/* ═══ Contact ═══ */}
        <Card padded id="contact" className="!overflow-visible">
          <SectionHeader title="Contact" aside="Email or phone required" />
          {errors.contact && touched.professionalEmail && (
            <p role="alert" className="mb-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
              {errors.contact}
            </p>
          )}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Work email" htmlFor="professionalEmail" error={fieldError('professionalEmail')}>
              <Input type="email" inputMode="email" {...inputProps('professionalEmail')} placeholder="name@company.com" />
            </Field>
            <Field label="Personal email" htmlFor="personalEmail" error={fieldError('personalEmail')}>
              <Input type="email" inputMode="email" {...inputProps('personalEmail')} placeholder="name@example.com" />
            </Field>
            <Field label="Work phone" htmlFor="professionalPhone" error={fieldError('professionalPhone')}>
              <Input type="tel" {...inputProps('professionalPhone')} placeholder="+224 700 000 000" />
            </Field>
            <Field label="Personal phone" htmlFor="personalPhone" error={fieldError('personalPhone')}>
              <Input type="tel" {...inputProps('personalPhone')} placeholder="+224 700 000 000" />
            </Field>
          </div>
        </Card>

        {/* ═══ Social ═══ */}
        <CollapsibleCard
          id="stakeholder-social"
          title="Social"
          aside={socialFilled > 0 ? `${socialFilled} ${socialFilled === 1 ? 'link' : 'links'}` : undefined}
          open={socialOpen}
          onToggle={() => setSocialExpanded(!socialOpen)}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="LinkedIn" htmlFor="linkedinUrl" error={fieldError('linkedinUrl')}>
              <Input type="url" {...inputProps('linkedinUrl')} placeholder="https://linkedin.com/in/…" />
            </Field>
            <Field label="X" htmlFor="twitterUrl" error={fieldError('twitterUrl')}>
              <Input type="url" {...inputProps('twitterUrl')} placeholder="https://x.com/…" />
            </Field>
            <Field label="Facebook" htmlFor="facebookUrl" error={fieldError('facebookUrl')}>
              <Input type="url" {...inputProps('facebookUrl')} placeholder="https://facebook.com/…" />
            </Field>
            <Field label="Telegram" htmlFor="telegramUrl" error={fieldError('telegramUrl')}>
              <Input type="url" {...inputProps('telegramUrl')} placeholder="https://t.me/…" />
            </Field>
            <Field label="WhatsApp" htmlFor="whatsapp" error={fieldError('whatsapp')}>
              <Input type="text" {...inputProps('whatsapp')} placeholder="wa.me link or number" />
            </Field>
          </div>
        </CollapsibleCard>

        {/* ═══ Roles ═══ */}
        {includeOrganizationFields && (
          <Card className="!overflow-visible">
            <div className="px-4 pb-1.5 pt-4">
              <SectionHeader title="Roles" aside={assignments.length > 0 ? assignments.length : undefined} />
              {errors.assignment && (
                <p role="alert" className="mb-2.5 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
                  {errors.assignment}
                </p>
              )}
            </div>
            <div className="border-t border-neutral-200/70">
              <AssignmentManager
                stakeholderId={stakeholder?.id || 'new'}
                assignments={assignments}
                onAssignmentsChange={setAssignments}
                canEdit={true}
                onAssignmentDataChange={stakeholder ? undefined : setPendingAssignment}
                initialFormData={prefillData ? {
                  organizationId: prefillData.organizationId || '',
                  role: prefillData.role || '',
                  department: prefillData.department || '',
                  startDate: prefillData.startDate || new Date().toISOString().split('T')[0],
                  endDate: prefillData.endDate || '',
                  isCurrent: prefillData.isCurrent !== undefined ? prefillData.isCurrent : true,
                } : undefined}
                organizationNameHint={prefillData?.organizationName}
              />
            </div>
          </Card>
        )}

        {/* ═══ Influence ═══ */}
        <CollapsibleCard
          id="stakeholder-influence"
          title="Influence"
          open={influenceExpanded}
          onToggle={() => setInfluenceExpanded(open => !open)}
        >
          <InfluenceScoresEditor
            entityType="stakeholder"
            entityId={influenceEntityId}
            onChange={handleInfluenceChange}
          />
        </CollapsibleCard>

        {/* ═══ Desktop actions ═══ */}
        <div className="hidden items-center justify-end gap-2 pt-1 sm:flex">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isLoading} className="min-w-[88px]">
            {submitLabel}
          </Button>
        </div>
      </form>

      {/* ═══ Mobile action bar (sits above the tab bar) ═══ */}
      <div
        className="fixed inset-x-0 z-40 border-t border-neutral-200/70 bg-white/85 px-4 py-2.5 font-ui backdrop-blur-xl backdrop-saturate-150 sm:hidden"
        style={{ bottom: 'var(--app-tabbar-h, 0px)' }}
      >
        <div className="flex items-center gap-2.5">
          <Button variant="secondary" onClick={onCancel} disabled={isLoading} className="!h-11 flex-1 !text-[15px]">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isLoading}
            className="!h-11 flex-1 !text-[15px]"
          >
            {submitLabel}
          </Button>
        </div>
      </div>

      {/* ═══ Media picker ═══ */}
      <Dialog
        open={showMediaPicker && !!stakeholder?.id}
        onClose={() => setShowMediaPicker(false)}
        labelledBy="stakeholder-media-title"
        handle
        className="max-h-[88vh] sm:max-h-[80vh] sm:max-w-4xl"
      >
        <DialogHeader id="stakeholder-media-title" title="Choose photo" onClose={() => setShowMediaPicker(false)} />
        <DialogBody>
          {stakeholder?.id && (
            <MediaGallery
              stakeholderId={stakeholder.id}
              onMediaSelect={handleMediaSelect}
              viewMode="grid"
            />
          )}
        </DialogBody>
      </Dialog>
    </>
  );
};

const SOCIAL_FIELDS = ['linkedinUrl', 'twitterUrl', 'facebookUrl', 'telegramUrl', 'whatsapp'] as const;

/** Small round camera button pinned to the avatar */
const CameraButton: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }> = ({ label, ...props }) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white text-neutral-600 shadow-[0_0_0_2px_#fff] ring-1 ring-inset ring-neutral-200 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
    {...props}
  >
    <Camera className="h-3.5 w-3.5" strokeWidth={1.75} />
  </button>
);

/** Card whose title row toggles the body */
const CollapsibleCard: React.FC<{
  id: string;
  title: string;
  aside?: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}> = ({ id, title, aside, open, onToggle, children }) => (
  <Card className="!overflow-visible">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={id}
      className="group flex w-full items-center justify-between gap-3 rounded-2xl p-4 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
    >
      <span className="flex min-h-[28px] items-center text-[15px] tracking-[-0.01em] text-ink">{title}</span>
      <span className="flex items-center gap-2 text-[12px] text-neutral-400">
        {aside}
        <ChevronDown
          className={`h-4 w-4 transition-transform duration-200 group-hover:text-ink ${open ? 'rotate-180' : ''}`}
          strokeWidth={1.75}
        />
      </span>
    </button>
    {open && (
      <div id={id} className="-mt-1.5 animate-fade-up px-4 pb-4">
        {children}
      </div>
    )}
  </Card>
);
